import React from 'react';
import {
  Box,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
  Fade,
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../contexts/TranslationContext';

interface ProductPaginationProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  showItemsPerPageSelector?: boolean;
  showViewModeToggle?: boolean;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
}

const ProductPagination: React.FC<ProductPaginationProps> = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [6, 12, 24, 48],
  showItemsPerPageSelector = true,
  showViewModeToggle = false,
  viewMode = 'grid',
  onViewModeChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Fade in timeout={500}>
      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          mb: 3,
        }}
      >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {/* Items Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {t('pagination.showing')} <strong>{startItem}</strong> {t('pagination.to')} <strong>{endItem}</strong> {t('pagination.of')}{' '}
            <strong>{totalItems}</strong> {t('pagination.products')}
          </Typography>
          {totalPages > 1 && (
            <Chip
              label={t('pagination.pageOf').replace('{page}', String(currentPage)).replace('{total}', String(totalPages))}
              size="small"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 'bold',
                '& .MuiChip-label': {
                  color: 'white',
                },
              }}
            />
          )}
        </Box>

        {/* Controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Items Per Page Selector */}
          {showItemsPerPageSelector && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('pagination.perPage')}</InputLabel>
              <Select
                value={itemsPerPage}
                label={t('pagination.perPage')}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                {itemsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option} {t('pagination.items')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* View Mode Toggle */}
          {showViewModeToggle && onViewModeChange && (
            <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box
                onClick={() => onViewModeChange('grid')}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  bgcolor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                  color: viewMode === 'grid' ? 'white' : 'text.secondary',
                  borderRadius: '4px 0 0 4px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: viewMode === 'grid' ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <ViewModuleIcon fontSize="small" />
              </Box>
              <Box
                onClick={() => onViewModeChange('list')}
                sx={{
                  p: 1,
                  cursor: 'pointer',
                  bgcolor: viewMode === 'list' ? 'primary.main' : 'transparent',
                  color: viewMode === 'list' ? 'white' : 'text.secondary',
                  borderRadius: '0 4px 4px 0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: viewMode === 'list' ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <ViewListIcon fontSize="small" />
              </Box>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => onPageChange(page)}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              showFirstButton
              showLastButton
              getItemAriaLabel={(type, page, selected) => {
                switch (type) {
                  case 'first':
                    return t('allProducts.pagination.first');
                  case 'last':
                    return t('allProducts.pagination.last');
                  case 'next':
                    return t('allProducts.pagination.next');
                  case 'previous':
                    return t('allProducts.pagination.previous');
                  default:
                    return `${t('pagination.pageOf').replace('{page}', String(page)).replace('{total}', String(totalPages))}`;
                }
              }}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 'bold',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'primary.50',
                    transform: 'translateY(-1px)',
                    transition: 'all 0.2s ease',
                  },
                },
              }}
            />
          )}
        </Box>
      </Box>
      </Paper>
    </Fade>
  );
};

export default ProductPagination;
